import { sendOpenSearchLogs } from 'utils/helper-functions/send-open-search-logs.ts';
import { RecoverySettingsModel } from 'utils/models/RecoverySettings.Model.ts';
import { isPanoramicTheme } from '@pano-project/variables/is-panoramic-2-0-theme';
import { previewBonusPromocodes } from '@pano-project/variables/preview-bonus-promocodes';
import { convertAmountBetweenCurrencies } from '@pano-project/utils/formatFees';
import {
  getActiveIntent,
  getActiveTransactionType,
  getGatewayTransactionTypeArgument,
  getCardRoutingTransactionTypeArgument,
  getPrimaryIntent,
  getSessionIntents,
  hasSwitchedInSession,
  isMultiIntentSession,
  mapGatewayTransactionTypeToIntent,
  mapIntentToTransactionType,
  reopenIntent,
} from '@pano-project/utils/intentMapping';
import {
  getIntentAvailability,
  getUnknownAvailabilityIntents,
  isEveryConfiguredIntentEmpty,
} from '@pano-project/utils/intentAvailability';
import {
  createValidationPayError,
  changePayData,
  updateGPayInfo,
  updateApplePayInfo,
} from './pay-data-actions';
import { createFatalError, createValidationProfileError } from './errors-actions';
import { transformSyntheticEvent } from '../../utils/helper-functions/transform-syntetic-event';
import PraxisService from '../../context-components/service';
import { triggerAdvanceCashierEvent } from '../../utils/custom-cashier-events/trigger-advance-cashier-event';
import { sanitizeNonLatinCharacters } from '../../utils/helper-functions/sanitize-non-latin-characters';
import { getDeviceData } from '../../utils/helper-functions/get-device-data';
import { cardToObSettings } from './cardToObSettingsAction';
import { isTest } from '../../variables/host';
import { ResponseStatus } from '../../variables/response-status';
import {
  convertFromMinorUnits,
  mathCeilDecimal,
} from '../../utils/round-functions/math-ceil-decimal';
import { round } from '../../utils/round-functions/round';
import {
  findCurrentFee,
  calculateAmountWithPercent,
} from '../../utils/fee-functions/calculate-comission';
import { tabSwitchGatewaysSettled } from './tab-switch-lock-actions';
import {
  filterUnsupportedDigitalWalletGateways,
  isDigitalWallet,
} from '../../utils/hooks/digital-wallets/checkDigitalWalletDeviceCompatibility';
import { isNativeSdkMethod } from '../../utils/hooks/digital-wallets/native-sdk';

const service = new PraxisService();
const {
  postPayGateway,
  getUser,
  getTransactionStatus,
  saveData,
  getGateways,
  getGatewayDetails,
  partialApprovedResult,
  manageRetryRequest,
  postErrors,
  getAvailableGateways,
  getOBTMethods,
  manageTemplate,
} = service;

export const setEnableSessionEvents = (payload) => {
  return {
    type: 'SET_ENABLE_SESSION_EVENTS',
    payload,
  };
};

export const createSessionId = (payload) => {
  return {
    type: 'SESSION_ID_INIT',
    payload,
  };
};

export const tokenInit = (payload) => {
  return {
    type: 'TOKEN_INIT',
    payload,
  };
};

export const createVirtualSettings = (payload) => {
  return {
    type: 'VIRTUAL_SETTINGS',
    payload,
  };
};

export const changeInitTheme = (payload) => {
  return {
    type: 'INIT_THEME',
    payload,
  };
};

export const updateGateways = (payload) => {
  return {
    type: 'UPDATE_GATEWAYS',
    payload,
  };
};

// A fetchGateways request started or ended. Requests may overlap, so they are
// counted and the transaction tabs stay locked until the last one ends.
export const gatewaysRequestStarted = () => {
  return {
    type: 'GATEWAYS_REQUEST_STARTED',
  };
};

export const gatewaysRequestEnded = () => {
  return {
    type: 'GATEWAYS_REQUEST_ENDED',
  };
};

export const updateProcessingGateways = (payload) => {
  return {
    type: 'UPDATE_PROCESSING_GATEWAYS',
    payload,
  };
};

export const setPendingTransactions = (payload) => {
  return {
    type: 'SET_PENDING_TRANSACTIONS',
    payload,
  };
};

export const setWithdrawalRequests = (payload) => {
  return {
    type: 'SET_WITHDRAWAL_REQUESTS',
    payload,
  };
};

export const fetchWithdrawalRequests = () => (dispatch) => {
  return new Promise((resolve) => {
    service
      .serverGetTransactions()
      .then(({ data } = {}) => {
        const withdrawalRequestsData = data?.withdrawal_requests || [];
        dispatch(setWithdrawalRequests(withdrawalRequestsData));
        resolve(withdrawalRequestsData);
      })
      .catch(() => {
        dispatch(setWithdrawalRequests([]));
        resolve([]);
      });
  });
};

export const createResultOfPay = (payload) => {
  return {
    type: 'CREATE_RESULT_OF_PAY',
    payload,
  };
};

const isOnlyTransactionType = (payload) => {
  const keys = Object.keys(payload);
  return keys.length === 1 && keys[0] === 'transaction_type';
};

export const changeProfile = (payload) => {
  if (!isOnlyTransactionType(payload)) {
    //Sanitize the customer fields based in the restrict_non_latin_characters flag
    payload = sanitizeNonLatinCharacters(payload, ['customer']);
  }
  return {
    type: 'UPDATE_PROFILE',
    payload,
  };
};

/**
 * Writes the transaction type and the intent of the tab the player is on into
 * the customer profile and keeps every other field of it. Used when that tab's
 * customer data came back with nothing to show, so no full profile arrives for
 * it, while the rest of the application must still see the tab the player is
 * on. The profile reducer replaces the whole profile, hence the explicit merge.
 */
export const mergeProfileTabFields =
  ({ transaction_type, intent } = {}) =>
  (dispatch, getState) => {
    const tabFields = {
      ...(transaction_type !== undefined && { transaction_type }),
      ...(intent !== undefined && { intent }),
    };

    if (!Object.keys(tabFields).length) return;

    dispatch(changeProfile({ ...(getState().profileInfo || {}), ...tabFields }));
  };

export const createMainFocus = (payload) => {
  payload = transformSyntheticEvent(payload);
  return {
    type: 'MAIN_FOCUS',
    payload,
  };
};

export const updateGatewayDetails = (payload) => {
  //Sanitize the gateway templates based in the restrict_non_latin_characters flag
  if (payload && typeof payload === 'object' && payload.templates) {
    payload = sanitizeNonLatinCharacters(payload, ['templates']);
  }
  return {
    type: 'CREATE_CURRENT_GATEWAY',
    payload,
  };
};

export const updateGatewayInfoFromAmountPage = (payload) => {
  return {
    type: 'CREATE_CURRENT_GATEWAY_FROM_AMOUNT_PAGE',
    payload,
  };
};

export const updateAdditionalFields = (additional_fields, psp_bank_id) => {
  return additional_fields.map((field) => {
    if (field.name === 'bank_id') {
      return { ...field, default_value: psp_bank_id };
    }
    return field;
  });
};

export const createCurrentGateway =
  (data, selectionSource = 'unknown') =>
  (dispatch, getState) => {
    // PRX-24667: no method or gateway switch during a Revolut Pay attempt.
    if (getState()?.nativeSdk?.isPaymentInProgress) return Promise.resolve({});

    return new Promise((resolve) => {
      const card_type_name = data?.card_type_name ?? null;
      const { psp_bank_id, recently_used_template, recently_used } = data;
      const state = getState();
      const layout = state?.initTheme?.theme_editor_settings?.customization_settings?.layout;
      const isAmountFirst = layout === 'amount-first';

      if (isAmountFirst) {
        dispatch({
          type: 'SET_GATEWAY_SELECTION_SOURCE',
          payload: selectionSource,
        });
        // PRX-23978 (user-reported): until these details arrive, the
        // previous gateway's form is still mounted -- AmountFirstPage
        // ignores a final submit click while this flag is set.
        dispatch({ type: 'SET_GATEWAY_DETAILS_LOADING', payload: true });
      }

      getGatewayDetails(data)
        .then(({ data }) => {
          if (data.status === ResponseStatus.OK) {
            let updatedAdditionalFields;
            if (psp_bank_id) {
              updatedAdditionalFields = updateAdditionalFields(data.additional_fields, psp_bank_id);
            }

            if (isAmountFirst) {
              const recentlyUsedTokens = (recently_used_template || [])
                .map((t) => t?.token)
                .filter(Boolean);
              if (recentlyUsedTokens.length === 0) {
                data.templates = {};
              } else {
                const templatesArray = data.templates || [];
                const matchedTemplates = templatesArray.filter(
                  (t) => t?.token && recentlyUsedTokens.includes(t.token),
                );
                data.templates = matchedTemplates.reduce((acc, template) => {
                  if (template.label) {
                    acc[template.label] = template;
                  }
                  return acc;
                }, {});
              }
            }

            if (window.cashierAdditionalFieldsModifier) {
              const dataToModify = {
                ...data,
                additional_fields: updatedAdditionalFields || data.additional_fields,
              };
              window
                .cashierAdditionalFieldsModifier(dataToModify)
                .then((el) => {
                  if (el.status === ResponseStatus.OK)
                    data.additional_fields = el.additional_fields;

                  dispatch(
                    updateGatewayDetails({
                      ...data,
                      card_type_name,
                      ...(isPanoramicTheme && {
                        recently_used_template,
                        recently_used,
                      }),
                    }),
                  );
                  dispatch(setLimitSubmit(false));
                })
                .catch((error) => {
                  sendOpenSearchLogs({
                    event_group: 'cashier_additional_fields_modifier',
                    error_data: {
                      error: {
                        message: 'cashierAdditionalFieldsModifier error in createCurrentGateway',
                        stack: error,
                      },
                    },
                  });
                  dispatch(
                    updateGatewayDetails({
                      ...data,
                      card_type_name,
                      ...(updatedAdditionalFields && {
                        additional_fields: updatedAdditionalFields,
                      }),
                    }),
                  );
                  dispatch(setLimitSubmit(false));
                });
            } else {
              dispatch(
                updateGatewayDetails({
                  ...data,
                  card_type_name,
                  ...(isPanoramicTheme && {
                    recently_used_template,
                    recently_used,
                  }),
                  ...(updatedAdditionalFields && {
                    additional_fields: updatedAdditionalFields,
                  }),
                }),
              );
            }

            dispatch(setLimitSubmit(false));
          }
          if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
          if (data.status === ResponseStatus.SERVER_ERROR)
            dispatch(createFatalError(data.error_details));

          sendOpenSearchLogs({
            event_group: 'cashier_events',
            event_name: 'payment_method_selected',
            event_data: {
              ...data,
              templates: Object.keys(data?.templates || {}),
            },
            event_context: {
              response_code: data.status,
            },
          });

          if (isAmountFirst) dispatch({ type: 'SET_GATEWAY_DETAILS_LOADING', payload: false });
          resolve(data);
        })
        .catch((res = {}) => {
          sendOpenSearchLogs({
            event_group: 'cashier_get_gateway_details',
            error_data: res,
          });

          const { response = {} } = res;
          const { data = {} } = response;

          if (data.status === ResponseStatus.OK) {
            if (isAmountFirst) {
              const recentlyUsedTokens = (recently_used_template || [])
                .map((t) => t?.token)
                .filter(Boolean);
              if (recentlyUsedTokens.length === 0) {
                data.templates = {};
              } else {
                const templatesArray = data.templates || [];
                const matchedTemplates = templatesArray.filter(
                  (t) => t?.token && recentlyUsedTokens.includes(t.token),
                );
                data.templates = matchedTemplates.reduce((acc, template) => {
                  if (template.label) {
                    acc[template.label] = template;
                  }
                  return acc;
                }, {});
              }
            }
            dispatch(
              updateGatewayDetails({
                ...data,
                ...(isPanoramicTheme && {
                  recently_used_template,
                  recently_used,
                }),
              }),
            );
            dispatch(setLimitSubmit(false));
          }
          if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
          if (data.status === ResponseStatus.SERVER_ERROR)
            dispatch(createFatalError(data.error_details));
          if (!data.status) dispatch(createFatalError());
          if (isAmountFirst) dispatch({ type: 'SET_GATEWAY_DETAILS_LOADING', payload: false });
          resolve(data);
        });
    });
  };

export const saveLocation = (payload) => {
  return {
    type: 'SAVE-LOCATION',
    payload,
  };
};

export const changeBaseAmount = (payload) => {
  return {
    type: 'CHANGE_BASE_AMOUNT',
    payload,
  };
};

export const changeAmountFirstStep = (payload) => {
  return {
    type: 'CHANGE_AMOUNT_FIRST_STEP',
    payload,
  };
};

const dispatchAllMethodsInMaintenance = (gateways, dispatch) => {
  const allMethodsInMaintenance =
    gateways?.length > 0 && gateways.every((g) => g.maintenance?.enabled === 1);
  dispatch({ type: 'SET_ADD_FIELDS_INFO', payload: { allMethodsInMaintenance } });
};

const filterMaintenanceGateways = (gateways = []) => {
  if (isPanoramicTheme) return gateways;
  return gateways.filter((gw) => gw?.maintenance?.enabled !== 1);
};

/**
 * Records whether one configured intent has any payment method.
 *
 * The record lives at the top level of initTheme, which merges, so it cannot
 * disturb the nested session object.
 */
export const recordIntentAvailability = (intent, hasMethods) => (dispatch, getState) => {
  // A single-intent session needs no record: it has one configured intent, so
  // the decision is the one it already makes today. Keeping it out also keeps
  // such a session's state writes identical to today's.
  if (!intent || !isMultiIntentSession(getState())) return;

  const availability = getIntentAvailability(getState());
  if (availability[intent] === hasMethods) return;

  dispatch(changeInitTheme({ intentAvailability: { ...availability, [intent]: hasMethods } }));
};

/**
 * Asks the backend about every configured intent whose availability is not
 * known yet, so that "no configured intent has any payment method" can be
 * established without making the player visit the other tab. It only records
 * the answer; the tab the player is on keeps its own loaded data.
 */
export const probeUnknownIntentsAvailability = () => (dispatch, getState) => {
  const state = getState();

  if (!isMultiIntentSession(state)) return Promise.resolve();

  const primaryIntent = getPrimaryIntent(state);
  // Before the first switch the primary intent is asked about with no intent
  // argument, as today; once switched, every intent is named explicitly.
  const sendPrimaryIntent = hasSwitchedInSession(state);
  const last_used_method_first =
    state?.initTheme?.theme_editor_settings?.customization_settings?.last_used_method_first;

  return Promise.all(
    getUnknownAvailabilityIntents(state).map((intent) => {
      // Each request keeps the transaction tabs locked until it answers,
      // like every other gateways request.
      dispatch(gatewaysRequestStarted());

      let request;
      try {
        request = getGateways(
          mapIntentToTransactionType(intent),
          0,
          0,
          last_used_method_first,
          false,
          intent === primaryIntent && !sendPrimaryIntent ? null : intent,
        );
      } catch (error) {
        dispatch(gatewaysRequestEnded());
        throw error;
      }

      return request
        .then((res = {}) => {
          const { data = {} } = res;
          dispatch(recordIntentAvailability(intent, (data.gateways?.length ?? 0) > 0));
        })
        .catch(() => {})
        .finally(() => dispatch(gatewaysRequestEnded()));
    }),
  );
};

/**
 * The payment method list came back empty.
 *
 * A single-intent session shows the full-page empty state exactly as today. A
 * multi-intent session keeps both tabs and the player where they are, and only
 * falls back to the full-page state once every configured intent is known to
 * have no payment method.
 */
export const handleNoAvailablePaymentMethods = (data) => (dispatch, getState) => {
  const state = getState();

  if (!isMultiIntentSession(state)) {
    dispatch(createResultOfPay(data));
    return Promise.resolve(true);
  }

  dispatch(recordIntentAvailability(getActiveIntent(state), false));

  return dispatch(probeUnknownIntentsAvailability()).then(() => {
    if (!isEveryConfiguredIntentEmpty(getState())) return false;

    dispatch(createResultOfPay(data));
    return true;
  });
};

/**
 * The amount-first customer data of a multi-intent session says the tab the
 * player is on has nothing to show (it has no currencies).
 *
 * That layout asks for a payment method list only once an amount exists, so
 * the other tabs are not asked about here: only this tab is recorded as empty.
 * Every other tab records itself when its own customer data or its list,
 * asked for with an amount, arrives. The full-page empty state follows once
 * every configured intent is known to be empty.
 */
export const recordAmountFirstTabWithoutPaymentMethods = (data) => (dispatch, getState) => {
  dispatch(recordIntentAvailability(getActiveIntent(getState()), false));

  if (!isEveryConfiguredIntentEmpty(getState())) return false;

  dispatch(createResultOfPay(data));
  return true;
};

const cashierGatewayValidator = (data, dispatch) => {
  if (window.cashierGatewayValidator) {
    return window.cashierGatewayValidator(data).then((el) => {
      if (el.status === ResponseStatus.OK) data.gateways = el.gateways;
      data.gateways = filterMaintenanceGateways(data.gateways);
      dispatch(updateGateways(data));
      dispatchAllMethodsInMaintenance(data.gateways, dispatch);
      return { data };
    });
  }
  data.gateways = filterMaintenanceGateways(data.gateways);
  dispatch(updateGateways(data));
  dispatchAllMethodsInMaintenance(data.gateways, dispatch);
  return { data };
};

export const resetCardToObSettings = () => (dispatch) => {
  dispatch(
    cardToObSettings({
      show_obt_banner: false,
      obt_gateway: null,
      card_gateway: null,
      psp_bank_id: null,
      show_back_to_card: false,
    }),
  );
};

export const checkObtCurrencyAndAmountSupport =
  ({ obt_gateway } = {}) =>
  (dispatch, getState) => {
    const {
      payData,
      cardToObSettings: obtSettings,
      gatewaysList,
      processingGatewaysList,
      initTheme: {
        theme_editor_settings: {
          customization_settings: { layout, currency_display_mode } = {},
        } = {},
      } = {},
      profileInfo: { base_currency } = {},
    } = getState();

    const selectedCurrency =
      currency_display_mode === 'base_currency' ? base_currency : payData?.currency;
    const amount = payData?.amount;
    const obtGateway = obt_gateway || obtSettings?.obt_gateway;
    const processing_currencies = obtGateway?.payment_information?.processing_currencies;

    const supportedCurrency = processing_currencies?.find((gatewayCurrency) => {
      const { currency, min_amount, max_amount } = gatewayCurrency;
      return (
        currency === selectedCurrency &&
        (!amount || (amount >= Number(min_amount) && amount <= Number(max_amount)))
      );
    });

    if (supportedCurrency) {
      dispatch(
        cardToObSettings({
          show_obt_banner: true,
        }),
      );
    } else {
      dispatch(
        cardToObSettings({
          show_obt_banner: false,
        }),
      );
    }
  };

export const fetchOBTMethods =
  (currency, options = {}) =>
  (dispatch, getState) => {
    return new Promise((resolve) => {
      const { shouldApplyResult } = options;
      const checkCurrency = typeof currency === 'string' ? currency : currency?.currency;
      getOBTMethods(checkCurrency)
        .then((res = {}) => {
          let data;

          if (isTest) {
            data = res.data;
          } else {
            data = res;
          }

          const { bank_supported, psp_bank_id } = data;
          const obtGateway = data.gateway_list[0];

          if (typeof shouldApplyResult === 'function' && !shouldApplyResult()) {
            dispatch(resetCardToObSettings());
            resolve(data);
            return;
          }

          if (bank_supported === 1 && psp_bank_id && obtGateway) {
            dispatch(
              cardToObSettings({
                obt_gateway: { ...obtGateway, option_id: data.gateway_id },
                psp_bank_id: data.psp_bank_id,
              }),
            );

            dispatch(
              checkObtCurrencyAndAmountSupport({
                obt_gateway: { ...obtGateway, option_id: data.gateway_id },
              }),
            );
          } else {
            dispatch(resetCardToObSettings());
          }

          resolve(data);
        })
        .catch(() => {
          dispatch(resetCardToObSettings());
          resolve(null);
        });
    });
  };

export const fetchGateways =
  (ob_decline_recovery = 0, retry_with_apm = 0, first_gateway_details = false) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      profileInfo: { base_amount, base_currency },
      initTheme: { cashier_theme },
    } = state;
    // The tab the player is on decides which list is asked for: in a
    // multi-intent session that is the active intent's type, never a profile
    // value the previous tab may have left behind; otherwise the profile's
    // transaction type, as always.
    const transaction_type = getActiveTransactionType(state);
    const last_used_method_first =
      state?.initTheme?.theme_editor_settings?.customization_settings?.last_used_method_first;
    const layout = state?.initTheme?.theme_editor_settings?.customization_settings?.layout;

    const obRecovery =
      layout === 'amount-first' ? (ob_decline_recovery ? 1 : 0) : ob_decline_recovery;
    const apmRecovery = layout === 'amount-first' ? (retry_with_apm ? 1 : 0) : retry_with_apm;

    // The request locks the transaction tabs for its whole life, so no tab
    // switch (and so no second request) can start while it runs. Every way it
    // can end (a list, a failure, an error) lowers the count and ends the wait
    // of a tab switch that this request answers.
    const settleGatewaysRequest = () => {
      dispatch(gatewaysRequestEnded());
      dispatch(tabSwitchGatewaysSettled());
    };

    dispatch(gatewaysRequestStarted());

    let gatewaysRequest;
    try {
      gatewaysRequest = getGateways(
        transaction_type,
        obRecovery,
        apmRecovery,
        last_used_method_first,
        first_gateway_details,
      );
    } catch (error) {
      settleGatewaysRequest();
      throw error;
    }

    return gatewaysRequest
      .then(async (res = {}) => {
        const { data } = res;
        const {
          initTheme: {
            theme_editor_settings: { customization_settings: { layout } = {} } = {},
          } = {},
        } = state;
        const isAmountFirst = layout === 'amount-first';

        // PRX-23109: in a deposit session, drop google_pay_cc/apple_pay_cc
        // from the displayed list when this device doesn't actually
        // support that wallet (or the check couldn't run).
        //
        // PRX-23109 (T4.30): also surface this check's own per-wallet
        // `support` result to Redux (gPayData.isDeviceCompatible/
        // applePayData.isDeviceCompatible) so a later, otherwise
        // unconnected code path -- credit-card-form.js's pciScript and
        // DigitalWalletButton.tsx, deciding the embedded (toggle-inside-
        // Credit-Card) configuration's own container set -- can exclude
        // a wallet this device is already confirmed not to support,
        // instead of only checking whether the merchant enabled it.
        // `support.google_pay`/`support.apple_pay` is `null` whenever
        // this fetch's own gateway list had no separate tile for that
        // wallet (the check never ran for it) -- deliberately left
        // undispatched in that case so it can't clobber a confirmed
        // true/false result an earlier fetch already established for
        // this same device this session (see change-pay-info.js's own
        // handling of isDeviceCompatible for why that value must
        // outlive a gateway switch).
        //
        // PRX-23109 (user-reported): pass this session's already-
        // confirmed isDeviceCompatible result(s) in as `knownSupport`
        // so filterUnsupportedDigitalWalletGateways can skip re-running
        // the SDK check for a wallet it already has a settled answer
        // for -- see that function's own doc comment for why re-running
        // it unconditionally on every fetchGateways call (including
        // every currency change once a wallet is already selected) was
        // constructing a second, throwaway PraxisGate instance
        // alongside the real, already-selected wallet's own live
        // instance and could spuriously drop that wallet's tile from
        // this same refetch's own gateway list, which
        // useGatewayAutoSelection.ts then read as "no longer available"
        // and silently switched the customer off it.
        if (transaction_type === 'pay-in' && data?.gateways?.length && isPanoramicTheme) {
          const knownGPayCompatible =
            state.payInfo?.digitalWallets?.gPayData?.isDeviceCompatible ?? null;
          const knownApplePayCompatible =
            state.payInfo?.digitalWallets?.applePayData?.isDeviceCompatible ?? null;

          const { gateways, support } = await filterUnsupportedDigitalWalletGateways(
            data.gateways,
            state.token,
            { google_pay: knownGPayCompatible, apple_pay: knownApplePayCompatible },
          );
          data.gateways = gateways;

          if (support.google_pay !== null) {
            dispatch(updateGPayInfo({ isDeviceCompatible: support.google_pay }));
          }
          if (support.apple_pay !== null) {
            dispatch(updateApplePayInfo({ isDeviceCompatible: support.apple_pay }));
          }
        }

        // PRX-23898 (user-requested): google_pay_cc/apple_pay_cc as
        // separate payment methods are a Panoramic 2.0-only concept
        // (@pano-project) -- an old theme (@cashier-project/@nova-project)
        // has no UI of its own to render them and never should. Drop
        // them from the fetched list before it reaches the old themes'
        // gateway list/reducer, regardless of transaction type (unlike
        // the pay-in-only device-compatibility filter above, which is a
        // separate, Panoramic-only concern).
        if (!isPanoramicTheme && data?.gateways?.length) {
          data.gateways = data.gateways.filter((gateway) => !isDigitalWallet(gateway));
        }

        // PRX-24667: a native-SDK method (Revolut Pay) is offered only for a
        // Pano 2 deposit outside decline recovery. Filtering keeps the
        // remaining objects as they are, so the first gateway details the
        // Panoramic list carries on its first item survive.
        const isNativeSdkListItem = (gateway) =>
          isNativeSdkMethod(gateway) || isNativeSdkMethod(gateway?.gateway_details);
        const hideNativeSdkMethods =
          transaction_type === 'pay-out' || !isPanoramicTheme || !!obRecovery || !!apmRecovery;
        if (hideNativeSdkMethods && data?.gateways?.length) {
          data.gateways = data.gateways.filter((gateway) => !isNativeSdkListItem(gateway));
        }

        const isObtSupported = data?.gateways?.some((gateway) => gateway.is_obt_supported) ? 1 : 0;
        dispatch(
          cardToObSettings({
            is_obt_supported: isObtSupported,
            is_card_to_ob_available: isAmountFirst ? !!isObtSupported : false,
          }),
        );

        sendOpenSearchLogs({
          event_group: 'cashier_initialization',
          event_name: 'open_cashier_state',
          event_data: {
            amount: base_amount,
            currency: base_currency,
            available_payment_methods: data.gateways?.map((gate) => gate?.payment_method_key),
          },
        });

        if (data.status === ResponseStatus.OK) {
          dispatch(
            recordIntentAvailability(getActiveIntent(state), (data.gateways?.length ?? 0) > 0),
          );
          return cashierGatewayValidator(data, dispatch);
        }
        if (data.status === ResponseStatus.FAIL && apmRecovery === 1) {
          return { is_retry_apm_cancel: 1 };
        }
        if (data.status === ResponseStatus.FAIL && apmRecovery === 0) {
          // The panoramic "no payment methods" reply names the tab it belongs
          // to; the full-page result it may become stays exactly the reply it
          // always was.
          const {
            transaction_type: emptyTabTransactionType,
            intent: emptyTabIntent,
            ...noPaymentMethodsReply
          } = data;
          dispatch(handleNoAvailablePaymentMethods(noPaymentMethodsReply));
        }
        if (data.status === ResponseStatus.SERVER_ERROR)
          dispatch(createFatalError(data.error_details));
        if (!data) dispatch(createFatalError());
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: 'cashier_get_gateways',
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;

        if (data.status === ResponseStatus.OK) return cashierGatewayValidator(data, dispatch);
        if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
        if (data.status === ResponseStatus.SERVER_ERROR)
          dispatch(createFatalError(data.error_details));
        if (!data.status) dispatch(createFatalError());
      })
      .finally(settleGatewaysRequest);
  };

export const fetchProcessingGateways = (data) => (dispatch, getState) => {
  const reqData = { ...data };

  if (typeof reqData?.payment_method === 'string') {
    reqData.payment_method = [reqData.payment_method];
  }
  // The secure card service falls back to its own session's primary type when
  // the request names none, so once the player has switched tab the active
  // tab's card routing value (sale, authorize or payout, not the
  // pay-with-gateway submission's values) is sent explicitly. Otherwise the
  // request is exactly today's.
  const gatewayTransactionType = getCardRoutingTransactionTypeArgument(getState());

  return new Promise((resolve) => {
    return getAvailableGateways({
      ...reqData,
      ...(gatewayTransactionType && { transaction_type: gatewayTransactionType }),
    })
      .then((res) => {
        const { data = {} } = res;
        const { status } = data;

        sendOpenSearchLogs({
          event_name: 'get_avaible_gateways',
          event_group: 'cashier_events',
          event_data: {
            gateway_list: data.available_gateways,
          },
        });

        if (status === ResponseStatus.OK) {
          dispatch(updateProcessingGateways(data));
          const {
            initTheme: {
              theme_editor_settings: { customization_settings: { layout } = {} } = {},
            } = {},
          } = getState();
          const isAmountFirst = layout === 'amount-first';
          if (isAmountFirst) {
            const isObtSupported = data?.available_gateways?.some(
              (gateway) => gateway.is_obt_supported,
            )
              ? 1
              : 0;
            dispatch(
              cardToObSettings({
                is_obt_supported: isObtSupported,
                is_card_to_ob_available: isAmountFirst ? !!isObtSupported : false,
              }),
            );
          }
        } else {
          if (status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
          if (status === ResponseStatus.SERVER_ERROR)
            dispatch(createFatalError(data.error_details));
        }
        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: 'cashier_get_available_gateways',
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {}, data: { status } = {} } = response;

        if (status === ResponseStatus.OK) {
          dispatch(updateProcessingGateways(data));
          const {
            initTheme: {
              theme_editor_settings: { customization_settings: { layout } = {} } = {},
            } = {},
          } = getState();
          const isAmountFirst = layout === 'amount-first';
          if (isAmountFirst) {
            const isObtSupported = data?.available_gateways?.some(
              (gateway) => gateway.is_obt_supported,
            )
              ? 1
              : 0;
            dispatch(
              cardToObSettings({
                is_obt_supported: isObtSupported,
                is_card_to_ob_available: isAmountFirst ? !!isObtSupported : false,
              }),
            );
          }
        } else {
          if (status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
          if (status === ResponseStatus.SERVER_ERROR)
            dispatch(createFatalError(data.error_details));
          if (!status) {
            postErrors({
              error: {
                stack: 'getAvailableGateways catch',
                message: JSON.stringify(response),
              },
            });
            dispatch(createFatalError());
          }
        }
        resolve(data);
      });
  });
};

export const handleResultResponse = (data, dispatch) => {
  const { status } = data;
  switch (status) {
    case ResponseStatus.OK:
      dispatch(createResultOfPay(data));
      break;
    case ResponseStatus.FAIL:
      dispatch(createResultOfPay(data));
      break;
    case ResponseStatus.SERVER_ERROR:
      dispatch(createFatalError(data.error_details));
      break;
    case ResponseStatus.ERROR_VALIDATION:
      dispatch(createValidationPayError(data.error_details));
      break;
    default:
      dispatch(createFatalError());
  }
};

const delayPayWithGateway = () => {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      getUser(1).then(({ data }) => {
        const { processing_in_progress } = data;
        if (!processing_in_progress) {
          clearInterval(interval);
          resolve({ data });
        }
      });
    }, 10000);
  });
};

const updateRecoverySettingsAfterPayment = ({ dispatch, recoveryOptions }) => {
  const updatedRecoverySettings = {
    ...(recoveryOptions.ob_decline_recovery && {
      ob_decline_recovery: 0,
    }),
    ...(recoveryOptions.retry_with_apm && {
      retry_with_apm: 0,
    }),
  };

  RecoverySettingsModel.updateRecoverySettings({
    dispatch,
    recoverySettings: updatedRecoverySettings,
  });
};

/**
 * A withdrawal submitted in a multi-intent session changes the player's
 * pending withdrawal requests, which the Deposit tab lists; they were loaded
 * once and a tab switch keeps them. Marking the list as not loaded makes the
 * next Deposit visit load it again. A single-intent session never sees both
 * tabs, so nothing changes there.
 */
const markWithdrawalRequestsStale = (dispatch, state) => {
  if (!isMultiIntentSession(state)) return;
  if (mapIntentToTransactionType(getActiveIntent(state)) !== 'pay-out') return;

  dispatch(setWithdrawalRequests(undefined));
};

// PRX-24667: `options.nativeSdk` ({ gatewayId }) is the awaitable pay path of
// a native-SDK method (Revolut Pay): the request goes to the gateway of the
// native SDK object and the response is returned to the caller without being
// stored, because a response with the order token goes on in the Revolut
// window; the caller passes any other response to handleResultResponse once
// its attempt has ended (PRX-24940). "Processing in progress" is waited for
// as for every method and resolves with the final response; a failed request
// rejects with its response. A validation error is still shown on the form.
// Without `options` nothing changes.
export const payWithGateway =
  (data, isQuickDeposit = false, options = {}) =>
  (dispatch, getState) => {
    const nativeSdk = options?.nativeSdk || null;
    const {
      payData: { promocode },
      currentGateway: { option_id, option_type },
      gatewaysList: { is_encrypted_input_names = 0 } = {},
      virtualSettings,
      profileInfo: { remove_customer_data },
      initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} },
    } = getState();

    const recoverySettings = RecoverySettingsModel.getRecoverySettings();

    const apm_decline_recovery =
      layout === 'amount-first'
        ? recoverySettings.retry_with_apm
          ? 1
          : 0
        : recoverySettings.retry_with_apm;
    const ob_decline_recovery =
      layout === 'amount-first'
        ? recoverySettings.ob_decline_recovery
          ? 1
          : 0
        : recoverySettings.ob_decline_recovery;

    const params = {
      ...(promocode && {
        promocode: promocode,
      }),
      option_id: nativeSdk ? nativeSdk.gatewayId : option_id,
      option_type: nativeSdk ? 'gateway' : option_type,
      is_encrypted_input_names,
      remove_customer_data,
      ...virtualSettings,
      ...data,
    };

    sendOpenSearchLogs({
      event_group: 'cashier_events',
      event_name: 'cashier_submit',
      event_data: {
        option_id: params.option_id,
        option_type: params.option_type,
        is_encrypted_input_names: params.is_encrypted_input_names,
        remove_customer_data: params.remove_customer_data,
        override_limits: params.override_limits,
        payment_information: data.transaction,
        device_data: getDeviceData(),
      },
      event_context: {
        response_code: data.status,
      },
    });

    if (data?.additional_fields) {
      const updatedAdditionalFields = {};

      Object.entries(data.additional_fields).forEach(([key, value]) => {
        if (typeof value !== 'string') {
          updatedAdditionalFields[key] = value;
          return;
        }

        // If remove_customer_data is true and the key is "email" or "phone,"
        // then just assign the value and skip the check for "**".
        if (remove_customer_data && ['email', 'phone'].includes(key)) {
          updatedAdditionalFields[key] = value;
          return;
        }

        // Otherwise, assign null if the string contains "**"; otherwise, keep the original value.
        updatedAdditionalFields[key] = value.includes('**') ? null : value;
      });

      params.additional_fields = updatedAdditionalFields;
    }

    //tell the BE that this transaction is quick deposit
    if (isQuickDeposit) {
      params['is_quick_deposit'] = 1;
    }

    const recoveryOptions = {
      ob_decline_recovery,
      retry_with_apm: apm_decline_recovery,
    };

    // The active tab's gateway transaction type, sent only once the player
    // has switched tab in a multi-intent session (on both tabs from then on).
    // Before any switch, and always in a single-intent session, the request
    // is exactly today's.
    const stateAtSubmit = getState();
    const gatewayTransactionType = getGatewayTransactionTypeArgument(stateAtSubmit);

    return new Promise((resolve, reject) => {
      return postPayGateway({
        ...params,
        ...(apm_decline_recovery && { apm_decline_recovery }),
        ...(ob_decline_recovery && { ob_decline_recovery }),
        ...(gatewayTransactionType && { transaction_type: gatewayTransactionType }),
      })
        .then(({ data } = {}) => {
          const { status, processing_in_progress } = data;

          markWithdrawalRequestsStale(dispatch, stateAtSubmit);

          if (ob_decline_recovery || apm_decline_recovery) {
            updateRecoverySettingsAfterPayment({ dispatch, recoveryOptions });
          }

          if (status === ResponseStatus.OK) {
            const { transaction } = data;
            triggerAdvanceCashierEvent({
              event_type: 'transaction_attempted',
              transaction: {
                amount: transaction?.amount,
                currency: transaction?.currency,
                charge_amount: transaction?.charge_amount,
                charge_currency: transaction?.charge_currency,
                payment_method: transaction?.payment_method_key,
                payment_processor: transaction?.payment_processor,
                trace_id: transaction?.trace_id,
                card_number: transaction?.card_number,
              },
            });
          }

          if (nativeSdk) {
            if ([0, 2].includes(status) && processing_in_progress) {
              return delayPayWithGateway().then(({ data }) => resolve(data));
            }
            if (status === ResponseStatus.ERROR_VALIDATION) {
              dispatch(createValidationPayError(data.error_details));
            }
            return resolve(data);
          }

          if ([0, 2].includes(status) && processing_in_progress) {
            return delayPayWithGateway().then(({ data }) => {
              handleResultResponse(data, dispatch);
              resolve(data);
            });
          }
          handleResultResponse(data, dispatch);

          resolve(data);
        })
        .catch((res = {}) => {
          sendOpenSearchLogs({
            event_group: 'cashier_post_pay_gateway',
            error_data: res,
          });

          if (res?.response) markWithdrawalRequestsStale(dispatch, stateAtSubmit);

          if (ob_decline_recovery || apm_decline_recovery) {
            updateRecoverySettingsAfterPayment({ dispatch, recoveryOptions });
          }

          const { response = {} } = res;
          const { data = {}, data: { status, processing_in_progress } = {} } = response;

          if (nativeSdk) {
            if ([0, 2].includes(status) && processing_in_progress) {
              return delayPayWithGateway().then(({ data }) => resolve(data));
            }
            if (status === ResponseStatus.ERROR_VALIDATION) {
              dispatch(createValidationPayError(data.error_details));
            }
            return reject({
              reason: 'pay_request_error',
              httpStatus: response.status ?? null,
              data,
            });
          }

          if ([0, 2].includes(status) && processing_in_progress) {
            return delayPayWithGateway().then(({ data }) => {
              handleResultResponse(data, dispatch);
              resolve(data);
            });
          }
          handleResultResponse(data, dispatch);
          resolve(data);
        });
    });
  };

export const payWithPartiallyApproved = (data) => (dispatch) => {
  return new Promise((resolve) => {
    return partialApprovedResult(data)
      .then(({ data } = {}) => {
        handleResultResponse(data, dispatch);
        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: 'cashier_pay_with_partially_approved',
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;

        handleResultResponse(data, dispatch);
        resolve(data);
      });
  });
};

export const payWithManageRetryRequest = (data) => (dispatch, getState) => {
  const { payData, currentGateway } = getState();
  const isCurrencyConversion =
    payData.currency && payData.base_currency && payData.currency !== payData.base_currency;

  const { amount: basicAmount, totalCharge, currency } = payData;
  const rate = currentGateway?.payment_information?.currency_list?.[currency]?.rate || 1;

  const retryData = {
    ...data,
    ...payData,
    amount: totalCharge ? round(basicAmount * rate, currency) : Number(payData.amount),
    ...(totalCharge &&
      isCurrencyConversion && {
        origin_amount: Number(basicAmount),
      }),
  };

  return new Promise((resolve) => {
    return manageRetryRequest(retryData)
      .then(({ data } = {}) => {
        handleResultResponse(data, dispatch);
        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: 'cashier_pay_with_manage_retry_request',
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;
        handleResultResponse(data, dispatch);
        resolve(data);
      });
  });
};

export const cancelWithManageRetryRequest = (data) => (dispatch, getState) => {
  const { resultOfPay } = getState();
  return new Promise((resolve) => {
    return manageRetryRequest({
      ...data,
      action: 'cancel',
      original_trace_id: `${resultOfPay?.transaction?.trace_id}`,
    })
      .then(({ data } = {}) => {
        handleResultResponse(data, dispatch);

        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: 'cashier_cancel_with_manage_retry_request',
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;
        handleResultResponse(data, dispatch);
        resolve(data);
      });
  });
};

/**
 * A provider return reopens the tab of the transaction it returns from, taken
 * from that transaction's own type. Only a multi-intent session whose intents
 * include the matching one changes anything.
 */
export const reopenTransactionIntent = (transactionType) => (dispatch, getState) => {
  const state = getState();
  const intent = reopenIntent(
    state,
    mapGatewayTransactionTypeToIntent(transactionType, getSessionIntents(state)),
  );

  if (intent && intent !== getActiveIntent(state)) {
    dispatch(changeInitTheme({ activeIntent: intent }));
  }

  return intent;
};

export const getCustomerData =
  (callback = 0) =>
  (dispatch, getState) => {
    return new Promise((resolve) => {
      return getUser(callback)
        .then((res = {}) => {
          const { data: reply } = res;
          // The customer-data "no payment methods" reply names the tab it belongs
          // to by its transaction type and intent. Only the profile of a
          // multi-intent session takes those two values (below); what every other
          // consumer receives — the result screen, the start-up checks and the
          // logs — stays exactly the reply it always was.
          const isNoPaymentMethodsReply =
            reply?.status === ResponseStatus.FAIL && reply?.resultPageType === 'no_payment_methods';
          const {
            transaction_type: emptyTabTransactionType,
            intent: emptyTabIntent,
            ...noPaymentMethodsReply
          } = isNoPaymentMethodsReply ? reply : {};
          const data = isNoPaymentMethodsReply ? noPaymentMethodsReply : reply;

          if (callback && data) dispatch(reopenTransactionIntent(data.transaction_type));

          if (callback && data.redirect_url) data.redirect_url = null;

          // A multi-intent session must keep both tabs while another one still
          // has a payment method, so the full-page empty state is deferred to
          // the availability decision. The profile still follows the tab the
          // player is on, with every other field kept, so nothing can read the
          // previous tab's transaction type. A single-intent session is untouched.
          if (isNoPaymentMethodsReply && isMultiIntentSession(getState())) {
            dispatch(
              mergeProfileTabFields({
                transaction_type: emptyTabTransactionType,
                intent: emptyTabIntent,
              }),
            );
            // Amount-first asks for no payment method list before an amount
            // exists, so there the other tabs are not asked about at this point.
            const isAmountFirstLayout =
              getState()?.initTheme?.theme_editor_settings?.customization_settings?.layout ===
              'amount-first';
            dispatch(
              isAmountFirstLayout
                ? recordAmountFirstTabWithoutPaymentMethods(data)
                : handleNoAvailablePaymentMethods(data),
            );
            resolve(data);
            return;
          }

          if (data.status === ResponseStatus.OK) {
            if (window.cashierUpdateCustomer)
              try {
                window.cashierUpdateCustomer(data.customer);
              } catch (e) {
                sendOpenSearchLogs({
                  event_group: 'cashier_update_customer',
                  error_data: {
                    error: {
                      message: 'getUser error',
                      stack: e,
                    },
                  },
                });
              }
            const {
              initTheme: { cashier_preview_mode },
            } = getState();
            const sessionPromocodeOptions = data?.session_configuration?.promocode_options;
            const promocodeOptions = data?.promocode_options?.length
              ? data.promocode_options
              : sessionPromocodeOptions;

            if (isPanoramicTheme && cashier_preview_mode && !promocodeOptions?.length) {
              dispatch(changeProfile({ ...data, promocode_options: previewBonusPromocodes() }));
            } else {
              let updatedData = {
                ...data,
                ...(promocodeOptions && { promocode_options: promocodeOptions }),
              };

              if (updatedData?.promocode_options?.length > 0) {
                updatedData.promocode_options = updatedData.promocode_options.map((promo) => {
                  const currencies = promo.constraints?.processing_currencies || {};

                  const reformattedCurrencies = Object.fromEntries(
                    Object.entries(currencies).map(([currency, { min_amount, max_amount }]) => {
                      return [
                        currency,
                        {
                          min_amount: convertFromMinorUnits(min_amount, currency),
                          max_amount: convertFromMinorUnits(max_amount, currency),
                        },
                      ];
                    }),
                  );

                  return {
                    ...promo,
                    constraints: {
                      ...promo.constraints,
                      processing_currencies: reformattedCurrencies,
                    },
                  };
                });
              }

              dispatch(changeProfile(updatedData));
            }
          }
          if (data.status === ResponseStatus.FAIL) {
            const { resultOfPay: currentResult } = getState();
            const currentTraceId = currentResult?.transaction?.trace_id;
            const incomingTraceId = data?.transaction?.trace_id;
            const currentStatus = currentResult?.transaction?.status;
            const isCurrentPending = ['pending', 'pending_async', 'initialized'].includes(
              currentStatus,
            );
            const isStaleResult =
              currentTraceId &&
              incomingTraceId &&
              currentTraceId !== incomingTraceId &&
              isCurrentPending;
            const layout =
              getState()?.initTheme?.theme_editor_settings?.customization_settings?.layout;
            const shouldNormalizeRetryWithApm =
              callback &&
              layout === 'amount-first' &&
              (data?.retry_with_apm || data?.ob_decline_recovery);

            if (!isStaleResult) {
              if (shouldNormalizeRetryWithApm) {
                dispatch(
                  createResultOfPay({
                    ...data,
                    retry_with_apm: data?.retry_with_apm ? 1 : 0,
                    ob_decline_recovery: data?.ob_decline_recovery ? 1 : 0,
                  }),
                );
              } else {
                dispatch(createResultOfPay(data));
              }
            }
          }
          if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
          if (data.status === ResponseStatus.ERROR_VALIDATION)
            dispatch(createValidationProfileError(data.error_details));
          if (data.status === ResponseStatus.SERVER_ERROR)
            dispatch(createFatalError(data.error_details));
          if (data.status === ResponseStatus.ERROR_PROFILE_AMOUNT) dispatch(changeProfile(data));

          sendOpenSearchLogs({
            event_group: 'cashier_initialization',
            event_name: 'get_customer_data',
            event_data: data,
            event_context: {
              http_status_code: res.status,
              response_code: data.status,
              response_message: res.statusText,
            },
          });

          resolve(data);
        })
        .catch((res = {}) => {
          sendOpenSearchLogs({
            event_group: 'cashier_get_user',
            error_data: res,
          });

          const { response = {} } = res;
          const { data = {} } = response;
          if (data.status === ResponseStatus.OK) {
            if (window.cashierUpdateCustomer)
              try {
                window.cashierUpdateCustomer(data.customer);
              } catch {}
            dispatch(changeProfile(data));
          }
          if (data.status === ResponseStatus.FAIL) {
            const { resultOfPay: currentResult } = getState();
            const currentTraceId = currentResult?.transaction?.trace_id;
            const incomingTraceId = data?.transaction?.trace_id;
            const currentStatus = currentResult?.transaction?.status;
            const isCurrentPending = ['pending', 'pending_async', 'initialized'].includes(
              currentStatus,
            );
            const isStaleResult =
              currentTraceId &&
              incomingTraceId &&
              currentTraceId !== incomingTraceId &&
              isCurrentPending;
            const layout =
              getState()?.initTheme?.theme_editor_settings?.customization_settings?.layout;
            const shouldNormalizeRetryWithApm =
              callback &&
              layout === 'amount-first' &&
              (data?.retry_with_apm || data?.ob_decline_recovery);

            if (!isStaleResult) {
              if (shouldNormalizeRetryWithApm) {
                dispatch(
                  createResultOfPay({
                    ...data,
                    retry_with_apm: data?.retry_with_apm ? 1 : 0,
                    ob_decline_recovery: data?.ob_decline_recovery ? 1 : 0,
                  }),
                );
              } else {
                dispatch(createResultOfPay(data));
              }
            }
          }
          if (data.status === ResponseStatus.ERROR_VALIDATION)
            dispatch(createValidationProfileError(data.error_details));
          if (data.status === ResponseStatus.SERVER_ERROR)
            dispatch(createFatalError(data.error_details));
          if (data.status === ResponseStatus.ERROR_PROFILE_AMOUNT) dispatch(changeProfile(data));
          if (!data.status) dispatch(createFatalError());
          resolve(data);
        });
    });
  };

export const pollTransactionStatus = (trace_id) => (dispatch, getState) => {
  return new Promise((resolve) => {
    return getTransactionStatus(trace_id ? { trace_id } : {})
      .then((res = {}) => {
        const { data = {} } = res;
        const nextPayload = data.resultOfPay || data;

        if (nextPayload && nextPayload.transaction) {
          const current = getState().resultOfPay || {};
          const mergedPayLoad = {
            ...current,
            ...nextPayload,
            transaction: {
              ...current.transaction,
              ...nextPayload.transaction,
            },
          };
          dispatch(createResultOfPay(mergedPayLoad));
        }
        resolve(nextPayload);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: 'cashier_poll_transaction_status',
          error_data: res,
        });
        resolve({});
      });
  });
};

export const saveDataInfo = (amount) => (dispatch) => {
  return new Promise((resolve) => {
    saveData({ ...amount })
      .then(({ data }) => {
        if (data.status === ResponseStatus.ERROR_VALIDATION)
          dispatch(createValidationPayError(data?.error_details?.payment_information));
        if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
        if (data.status === ResponseStatus.OK) resolve(data);
        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: 'cashier_save_data_info',
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;
        if (data.status === ResponseStatus.ERROR_VALIDATION)
          dispatch(createValidationPayError(data?.error_details?.payment_information));
        if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
        if (data.status === ResponseStatus.OK) resolve(data);
        if (!data.status) dispatch(createFatalError());
        resolve(data);
      });
  });
};

export const setLimitSubmit = (payload) => {
  return {
    type: 'SET_LIMIT_SUBMIT',
    payload,
  };
};

export const setLimitError = (payload) => {
  return {
    type: 'SET_LIMIT_ERROR',
    payload,
  };
};

export const setPortalWrappers = (payload) => {
  return {
    type: 'SET_PORTAL_WRAPPERS',
    payload,
  };
};
export const setForceValidationDisable = (payload) => {
  return {
    type: 'SET_FORCE_VALIDATION_DISABLE',
    payload,
  };
};
export const sendManageTemplateRequest =
  ({ template_token, template_type, action }) =>
  (dispatch) => {
    return new Promise((resolve) => {
      service
        .manageTemplate({ template_token, template_type, action })
        .then(({ data } = {}) => {
          const manageTemplateResult = data;
          resolve(manageTemplateResult);
        })
        .catch(() => {
          resolve();
        });
    });
  };

export const setIsManualAmountChange = (payload) => {
  return {
    type: 'SET_IS_MANUAL_AMOUNT_CHANGE',
    payload,
  };
};
